import { useState } from 'react';
import { motion } from 'framer-motion';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useAuth } from '@/context/AuthContext';
import { KPICard } from '@/components/crm/KPICard';
import { PipelineChart } from '@/components/crm/PipelineChart';
import { LeadSourceChart } from '@/components/crm/LeadSourceChart';
import { ConversionFunnel } from '@/components/crm/ConversionFunnel';
import { TeamPerformance } from '@/components/crm/TeamPerformance';
import { ActivityTimeline } from '@/components/crm/ActivityTimeline';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Badge } from '@/components/ui/badge';
import { CRMPageHeader } from '@/components/crm/CRMPageHeader';
import {
  Calendar as CalendarIcon,
  RefreshCw,
  Download,
  TrendingUp,
  Users,
  DollarSign,
  Target,
  BarChart3,
  Filter,
  ChevronDown,
  Shield,
  Crown,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { DateRange } from '@/hooks/useAnalytics';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

// Role hierarchy for permission checking
type UserRole = 'viewer' | 'editor' | 'admin';
const roleHierarchy: Record<UserRole, number> = {
  viewer: 1,
  editor: 2,
  admin: 3,
};

function hasRequiredRole(userRole: UserRole, requiredRole: UserRole): boolean {
  return roleHierarchy[userRole] >= roleHierarchy[requiredRole];
}

function getUserCRMRole(isAdmin: boolean, isApproved: boolean, isSubscribed: boolean): UserRole {
  if (isAdmin) return 'admin';
  if (isApproved && isSubscribed) return 'editor';
  return 'viewer';
}

export function AnalyticsPage() {
  const { user, isAdmin, isApproved, isSubscribed } = useAuth();
  const userRole = getUserCRMRole(isAdmin, isApproved, isSubscribed);
  const canExport = hasRequiredRole(userRole, 'editor');

  const {
    dateRange,
    setDateRange,
    customDateRange,
    setCustomDateRange,
    isLoading,
    data,
    activities,
    totalPipelineValue,
    weightedPipelineValue,
    refresh,
    exportPipeline,
    exportLeadSources,
    exportTeamPerformance,
    exportRevenueTrend,
  } = useAnalytics();

  const [isCustomDateOpen, setIsCustomDateOpen] = useState(false);

  const handleDateRangeChange = (value: DateRange) => {
    setDateRange(value);
    if (value !== 'custom') {
      setIsCustomDateOpen(false);
    }
  };

  const handleCustomDateSelect = (date: Date | undefined, type: 'from' | 'to') => {
    setCustomDateRange((prev) => ({
      ...prev,
      [type]: date,
    }));
  };

  const dateRangeLabel = {
    '7d': 'Last 7 days',
    '30d': 'Last 30 days',
    '90d': 'Last 90 days',
    'custom': 'Custom range',
  }[dateRange];

  const totalLeads = data?.leadSources.reduce((sum, s) => sum + s.count, 0) || 0;

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Header */}
      <CRMPageHeader
        title="Analytics Dashboard"
        description="Track your sales performance and team metrics"
        titleAddon={isAdmin ? (
          <Badge variant="outline" className="bg-purple-500/10 text-purple-600 border-purple-200">
            <Shield className="h-3 w-3 mr-1" />
            Admin
          </Badge>
        ) : isSubscribed && !isAdmin ? (
          <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-200">
            <Crown className="h-3 w-3 mr-1" />
            Pro
          </Badge>
        ) : null}
        actions={(
          <>
            <Select value={dateRange} onValueChange={(v) => handleDateRangeChange(v as DateRange)}>
              <SelectTrigger className="w-[180px]">
                <CalendarIcon className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Select range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="90d">Last 90 days</SelectItem>
                <Popover open={isCustomDateOpen} onOpenChange={setIsCustomDateOpen}>
                  <PopoverTrigger asChild>
                    <SelectItem value="custom">
                      <span className="flex items-center">
                        <Filter className="h-3.5 w-3.5 mr-2" />
                        Custom range
                      </span>
                    </SelectItem>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <div className="p-4 space-y-4">
                      <div>
                        <p className="text-sm font-medium mb-2">From</p>
                        <Calendar
                          mode="single"
                          selected={customDateRange.from}
                          onSelect={(date) => handleCustomDateSelect(date, 'from')}
                          initialFocus
                        />
                      </div>
                      <div>
                        <p className="text-sm font-medium mb-2">To</p>
                        <Calendar
                          mode="single"
                          selected={customDateRange.to}
                          onSelect={(date) => handleCustomDateSelect(date, 'to')}
                          initialFocus
                        />
                      </div>
                      <Button 
                        className="w-full bg-[#2B7FFF] hover:bg-[#1A5FCC]"
                        onClick={() => setIsCustomDateOpen(false)}
                        disabled={!customDateRange.from || !customDateRange.to}
                      >
                        Apply Range
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              size="icon"
              onClick={refresh}
              disabled={isLoading}
              className="h-10 w-10"
            >
              <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
            </Button>

            {canExport && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="gap-2">
                    <Download className="h-4 w-4" />
                    Export
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-48" align="end">
                  <div className="space-y-1">
                    <Button variant="ghost" className="w-full justify-start text-sm" onClick={exportPipeline}>
                      Pipeline Data
                    </Button>
                    <Button variant="ghost" className="w-full justify-start text-sm" onClick={exportLeadSources}>
                      Lead Sources
                    </Button>
                    <Button variant="ghost" className="w-full justify-start text-sm" onClick={exportTeamPerformance}>
                      Team Performance
                    </Button>
                    <Button variant="ghost" className="w-full justify-start text-sm" onClick={exportRevenueTrend}>
                      Revenue Trend
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </>
        )}
      />

      {/* Date Range Badge */}
      <motion.div variants={itemVariants}>
        <Badge variant="secondary" className="text-xs">
          <CalendarIcon className="h-3 w-3 mr-1" />
          {dateRangeLabel}
          {dateRange === 'custom' && customDateRange.from && customDateRange.to && (
            <span className="ml-1">
              ({customDateRange.from.toLocaleDateString()} - {customDateRange.to.toLocaleDateString()})
            </span>
          )}
        </Badge>
      </motion.div>

      {/* KPI Cards */}
      <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Total Revenue"
          value={data?.kpi.totalRevenue || 0}
          change={data?.kpi.totalRevenueChange}
          format="currency"
          icon={<DollarSign className="h-6 w-6" />}
          iconBgColor="bg-green-500/10"
          iconColor="text-green-500"
          isLoading={isLoading}
          delay={0}
        />
        <KPICard
          title="New Leads"
          value={data?.kpi.newLeads || 0}
          change={data?.kpi.newLeadsChange}
          format="number"
          icon={<Users className="h-6 w-6" />}
          iconBgColor="bg-[#2B7FFF]/10"
          iconColor="text-[#2B7FFF]"
          isLoading={isLoading}
          delay={100}
        />
        <KPICard
          title="Conversion Rate"
          value={data?.kpi.conversionRate || 0}
          change={data?.kpi.conversionRateChange}
          format="percentage"
          icon={<Target className="h-6 w-6" />}
          iconBgColor="bg-amber-500/10"
          iconColor="text-amber-500"
          isLoading={isLoading}
          delay={200}
        />
        <KPICard
          title="Avg Deal Size"
          value={data?.kpi.avgDealSize || 0}
          change={data?.kpi.avgDealSizeChange}
          format="currency"
          icon={<TrendingUp className="h-6 w-6" />}
          iconBgColor="bg-purple-500/10"
          iconColor="text-purple-500"
          isLoading={isLoading}
          delay={300}
        />
      </motion.div>

      {/* Main Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pipeline Chart */}
        <motion.div variants={itemVariants} className="lg:col-span-2">
          <PipelineChart
            data={data?.pipeline || []}
            totalValue={totalPipelineValue}
            weightedValue={weightedPipelineValue}
            onExport={canExport ? exportPipeline : undefined}
            isLoading={isLoading}
          />
        </motion.div>

        {/* Lead Sources */}
        <motion.div variants={itemVariants}>
          <LeadSourceChart
            data={data?.leadSources || []}
            totalLeads={totalLeads}
            onExport={canExport ? exportLeadSources : undefined}
            isLoading={isLoading}
          />
        </motion.div>
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Conversion Funnel */}
        <motion.div variants={itemVariants}>
          <ConversionFunnel
            data={data?.funnel || []}
            isLoading={isLoading}
          />
        </motion.div>

        {/* Team Performance */}
        <motion.div variants={itemVariants}>
          <TeamPerformance
            data={data?.teamPerformance || []}
            onExport={canExport ? exportTeamPerformance : undefined}
            isLoading={isLoading}
          />
        </motion.div>

        {/* Activity Timeline */}
        <motion.div variants={itemVariants}>
          <ActivityTimeline
            activities={activities}
            onRefresh={refresh}
            isLoading={isLoading}
          />
        </motion.div>
      </div>

      {/* Revenue Trend Chart */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-[#2B7FFF]" />
                Revenue Trend
              </CardTitle>
              <CardDescription>Daily revenue over time</CardDescription>
            </div>
            {canExport && (
              <Button variant="outline" size="icon" onClick={exportRevenueTrend} className="h-9 w-9">
                <Download className="h-4 w-4" />
              </Button>
            )}
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {isLoading ? (
                <div className="h-full flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2B7FFF]" />
                </div>
              ) : (
                <RevenueTrendChart data={data?.revenueTrend || []} />
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
}

// Revenue Trend Chart Component
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { RevenueTrend } from '@/hooks/useAnalytics';

function RevenueTrendChart({ data }: { data: RevenueTrend[] }) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ value: number }>; label?: string }) => {
    if (active && payload && payload.length) {
      return (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-card border border-border rounded-xl p-4 shadow-xl"
        >
          <p className="font-semibold text-foreground mb-2">{formatDate(label || '')}</p>
          <div className="space-y-1 text-sm">
            <div className="flex items-center justify-between gap-6">
              <span className="text-muted-foreground">Revenue:</span>
              <span className="font-medium text-foreground">{formatCurrency(payload[0].value)}</span>
            </div>
            <div className="flex items-center justify-between gap-6">
              <span className="text-muted-foreground">Deals:</span>
              <span className="font-medium text-foreground">{payload[0].payload.deals}</span>
            </div>
          </div>
        </motion.div>
      );
    }
    return null;
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#2B7FFF" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#2B7FFF" stopOpacity={0.01} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
        <XAxis
          dataKey="date"
          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
          tickLine={false}
          axisLine={{ stroke: 'hsl(var(--border))' }}
          tickFormatter={formatDate}
          interval="preserveStartEnd"
        />
        <YAxis
          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
          tickLine={false}
          axisLine={false}
          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
        />
        <Tooltip content={<CustomTooltip />} />
        <Area
          type="monotone"
          dataKey="revenue"
          stroke="#2B7FFF"
          strokeWidth={2}
          fill="url(#revenueGradient)"
          animationDuration={1500}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export default AnalyticsPage;
